import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, orderBy, query, where } from "firebase/firestore";
import { db } from "@/firebase";

type CompareStatus = "win" | "tie" | "lose" | "no_own_price" | "no_competitor_price";
type JudgeFilter = "all" | "lose" | "tie" | "win";

type ShikakuDoc = {
  id: string;
  ShikakuName?: string;
};

type CompetitorCompanyDoc = {
  id: string;
  name?: string;
  isActive?: boolean;
};

type CompetitorProductDoc = {
  id: string;
  companyId?: string;
  name?: string;
  isActive?: boolean;
};

type ShikakuCompetitorMapDoc = {
  id: string;
  shikakuId?: string;
  competitorProductId?: string;
  isActive?: boolean;
};

type PriceSnapshotDoc = {
  id: string;
  targetType?: "shikaku" | "competitor";
  targetId?: string;
  price?: number;
  capturedDate?: string;
  capturedAt?: { seconds?: number; nanoseconds?: number };
  qualityStatus?: "ok" | "fallback_manual" | "needs_review";
};

type ComparisonRow = {
  shikakuId: string;
  shikakuName: string;
  ownPrice: number | null;
  minCompetitorPrice: number | null;
  minCompetitorName: string;
  minCompetitorCompanyName: string;
  priceDiff: number | null;
  status: CompareStatus;
  mappedCompanyIds: string[];
  dataQuality: "ok" | "contains_fallback";
};

const yen = new Intl.NumberFormat("ja-JP", { style: "currency", currency: "JPY", maximumFractionDigits: 0 });

const statusLabelMap: Record<CompareStatus, string> = {
  win: "勝ち",
  tie: "同値",
  lose: "負け",
  no_own_price: "自社価格なし",
  no_competitor_price: "競合価格なし",
};

export default function CompetitiveAnalysisPage() {
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const [shikakuList, setShikakuList] = useState<ShikakuDoc[]>([]);
  const [companyMap, setCompanyMap] = useState<Record<string, CompetitorCompanyDoc>>({});
  const [productMap, setProductMap] = useState<Record<string, CompetitorProductDoc>>({});
  const [mappingRows, setMappingRows] = useState<ShikakuCompetitorMapDoc[]>([]);

  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState("");

  const [ownPriceByTargetId, setOwnPriceByTargetId] = useState<Record<string, PriceSnapshotDoc>>({});
  const [competitorPriceByTargetId, setCompetitorPriceByTargetId] = useState<Record<string, PriceSnapshotDoc>>({});

  const [judgeFilter, setJudgeFilter] = useState<JudgeFilter>("lose");
  const [companyFilter, setCompanyFilter] = useState("all");

  useEffect(() => {
    const bootstrap = async () => {
      setLoading(true);
      setErrorMessage("");

      try {
        const [
          shikakuSnapshot,
          companySnapshot,
          productSnapshot,
          mappingSnapshot,
          dateSnapshot,
        ] = await Promise.all([
          getDocs(collection(db, "Shikaku")),
          getDocs(collection(db, "CompetitorCompanies")),
          getDocs(collection(db, "CompetitorProducts")),
          getDocs(collection(db, "ShikakuCompetitorMap")),
          getDocs(query(collection(db, "PriceSnapshots"), orderBy("capturedDate", "desc"))),
        ]);

        const shikakuDocs = shikakuSnapshot.docs.map((doc) => ({ id: doc.id, ...(doc.data() as Omit<ShikakuDoc, "id">) }));
        setShikakuList(shikakuDocs);

        const companies = companySnapshot.docs.map((doc) => ({ id: doc.id, ...(doc.data() as Omit<CompetitorCompanyDoc, "id">) }));
        const companyRecord = companies.reduce<Record<string, CompetitorCompanyDoc>>((acc, curr) => {
          acc[curr.id] = curr;
          return acc;
        }, {});
        setCompanyMap(companyRecord);

        const products = productSnapshot.docs.map((doc) => ({ id: doc.id, ...(doc.data() as Omit<CompetitorProductDoc, "id">) }));
        const productRecord = products.reduce<Record<string, CompetitorProductDoc>>((acc, curr) => {
          acc[curr.id] = curr;
          return acc;
        }, {});
        setProductMap(productRecord);

        const mappings = mappingSnapshot.docs.map((doc) => ({ id: doc.id, ...(doc.data() as Omit<ShikakuCompetitorMapDoc, "id">) }));
        setMappingRows(mappings.filter((item) => item.isActive !== false));

        const seen = new Set<string>();
        const dateList: string[] = [];
        dateSnapshot.docs.forEach((doc) => {
          const capturedDate = (doc.data() as PriceSnapshotDoc).capturedDate;
          if (!capturedDate || seen.has(capturedDate)) return;
          seen.add(capturedDate);
          dateList.push(capturedDate);
        });

        setAvailableDates(dateList.slice(0, 30));
        if (dateList.length > 0) {
          setSelectedDate(dateList[0]);
        }
      } catch (error) {
        console.error(error);
        setErrorMessage("初期データの読み込みに失敗しました。");
      } finally {
        setLoading(false);
      }
    };

    bootstrap();
  }, []);

  useEffect(() => {
    const fetchSnapshotsByDate = async () => {
      if (!selectedDate) return;
      try {
        const [ownSnap, competitorSnap] = await Promise.all([
          getDocs(
            query(
              collection(db, "PriceSnapshots"),
              where("capturedDate", "==", selectedDate),
              where("targetType", "==", "shikaku"),
            ),
          ),
          getDocs(
            query(
              collection(db, "PriceSnapshots"),
              where("capturedDate", "==", selectedDate),
              where("targetType", "==", "competitor"),
            ),
          ),
        ]);

        const ownMap = ownSnap.docs.reduce<Record<string, PriceSnapshotDoc>>((acc, doc) => {
          const row = { id: doc.id, ...(doc.data() as Omit<PriceSnapshotDoc, "id">) };
          if (row.targetId) acc[row.targetId] = row;
          return acc;
        }, {});
        const competitorMap = competitorSnap.docs.reduce<Record<string, PriceSnapshotDoc>>((acc, doc) => {
          const row = { id: doc.id, ...(doc.data() as Omit<PriceSnapshotDoc, "id">) };
          if (row.targetId) acc[row.targetId] = row;
          return acc;
        }, {});

        setOwnPriceByTargetId(ownMap);
        setCompetitorPriceByTargetId(competitorMap);
      } catch (error) {
        console.error(error);
        setErrorMessage("価格情報の読み込みに失敗しました。");
      }
    };

    fetchSnapshotsByDate();
  }, [selectedDate]);

  const rows = useMemo<ComparisonRow[]>(() => {
    const mappingByShikaku = mappingRows.reduce<Record<string, ShikakuCompetitorMapDoc[]>>((acc, curr) => {
      if (!curr.shikakuId || !curr.competitorProductId) return acc;
      if (!acc[curr.shikakuId]) acc[curr.shikakuId] = [];
      acc[curr.shikakuId].push(curr);
      return acc;
    }, {});

    return shikakuList.map((shikaku) => {
      const ownSnapshot = ownPriceByTargetId[shikaku.id];
      const ownPrice = typeof ownSnapshot?.price === "number" ? ownSnapshot.price : null;

      const mappings = mappingByShikaku[shikaku.id] || [];
      const mappedCompanyIds = Array.from(
        new Set(
          mappings
            .map((item) => item.competitorProductId)
            .map((productId) => (productId ? productMap[productId]?.companyId : undefined))
            .filter((value): value is string => Boolean(value)),
        ),
      );

      const competitorEntries = mappings
        .map((m) => m.competitorProductId)
        .filter((value): value is string => Boolean(value))
        .map((productId) => {
          const product = productMap[productId];
          const snapshot = competitorPriceByTargetId[productId];
          return {
            productId,
            product,
            price: typeof snapshot?.price === "number" ? snapshot.price : null,
            qualityStatus: snapshot?.qualityStatus,
          };
        })
        .filter((entry) => entry.price !== null);

      const minEntry = competitorEntries.reduce<(typeof competitorEntries)[number] | null>((min, curr) => {
        if (!min) return curr;
        if ((curr.price ?? Number.MAX_SAFE_INTEGER) < (min.price ?? Number.MAX_SAFE_INTEGER)) {
          return curr;
        }
        return min;
      }, null);

      const minCompetitorPrice = minEntry?.price ?? null;
      const minCompetitorName = minEntry?.product?.name || "-";
      const minCompetitorCompanyName = minEntry?.product?.companyId
        ? companyMap[minEntry.product.companyId]?.name || "-"
        : "-";

      let status: CompareStatus = "no_competitor_price";
      let diff: number | null = null;

      if (ownPrice === null) {
        status = "no_own_price";
      } else if (minCompetitorPrice === null) {
        status = "no_competitor_price";
      } else {
        diff = ownPrice - minCompetitorPrice;
        if (diff > 0) status = "lose";
        else if (diff === 0) status = "tie";
        else status = "win";
      }

      const dataQuality = competitorEntries.some((entry) => entry.qualityStatus === "fallback_manual")
        ? "contains_fallback"
        : "ok";

      return {
        shikakuId: shikaku.id,
        shikakuName: shikaku.ShikakuName || "(名称未設定)",
        ownPrice,
        minCompetitorPrice,
        minCompetitorName,
        minCompetitorCompanyName,
        priceDiff: diff,
        status,
        mappedCompanyIds,
        dataQuality,
      };
    });
  }, [companyMap, competitorPriceByTargetId, mappingRows, ownPriceByTargetId, productMap, shikakuList]);

  const filteredRows = useMemo(() => {
    return rows
      .filter((row) => {
        if (judgeFilter !== "all" && row.status !== judgeFilter) return false;
        if (companyFilter !== "all" && !row.mappedCompanyIds.includes(companyFilter)) return false;
        return true;
      })
      .sort((a, b) => {
        const left = a.priceDiff ?? -Number.MAX_SAFE_INTEGER;
        const right = b.priceDiff ?? -Number.MAX_SAFE_INTEGER;
        return right - left;
      });
  }, [rows, judgeFilter, companyFilter]);

  const metrics = useMemo(() => {
    const loseRows = rows.filter((row) => row.status === "lose" && typeof row.priceDiff === "number");
    const diffs = loseRows.map((row) => row.priceDiff as number);
    const avgDiff = diffs.length ? diffs.reduce((sum, current) => sum + current, 0) / diffs.length : 0;
    const maxDiff = diffs.length ? Math.max(...diffs) : 0;
    const needReviewCount = rows.filter(
      (row) => row.status === "no_own_price" || row.status === "no_competitor_price" || row.dataQuality === "contains_fallback",
    ).length;

    return {
      loseCount: loseRows.length,
      avgDiff,
      maxDiff,
      needReviewCount,
    };
  }, [rows]);

  if (loading) {
    return <div className="p-6 text-gray-600">競合分析データを読み込み中...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">競合分析</h1>
          <p className="text-sm text-gray-500 mt-1">自社と競合の価格競争状況を日付軸で確認します。</p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/admin/analysis/competitive-mapping"
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
          >
            <i className="ri-git-merge-line" />
            競合マッピング設定へ
          </Link>
          <Link
            to="/admin/analysis/competitive-scrape"
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            <i className="ri-download-2-line" />
            競合情報取得設定へ
          </Link>
        </div>
      </div>

      {errorMessage && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{errorMessage}</div>}

      <section className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <p className="text-xs text-gray-500">負け件数</p>
          <p className="text-2xl font-semibold text-red-600 mt-2">{metrics.loseCount}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <p className="text-xs text-gray-500">平均差額（負けのみ）</p>
          <p className="text-2xl font-semibold text-gray-900 mt-2">{yen.format(metrics.avgDiff)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <p className="text-xs text-gray-500">最大差額</p>
          <p className="text-2xl font-semibold text-gray-900 mt-2">{yen.format(metrics.maxDiff)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <p className="text-xs text-gray-500">要確認件数</p>
          <p className="text-2xl font-semibold text-amber-600 mt-2">{metrics.needReviewCount}</p>
        </div>
      </section>

      <section className="bg-white rounded-xl border border-gray-100 p-4 md:p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className="text-sm text-gray-700">
            比較日
            <select
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            >
              {availableDates.length === 0 ? <option value="">日付なし</option> : null}
              {availableDates.map((date) => (
                <option key={date} value={date}>
                  {date}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm text-gray-700">
            判定
            <select
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              value={judgeFilter}
              onChange={(e) => setJudgeFilter(e.target.value as JudgeFilter)}
            >
              <option value="all">全件</option>
              <option value="lose">負けのみ</option>
              <option value="tie">同値のみ</option>
              <option value="win">勝ちのみ</option>
            </select>
          </label>

          <label className="text-sm text-gray-700">
            競合企業
            <select
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
            >
              <option value="all">全企業</option>
              {Object.values(companyMap)
                .filter((company) => company.isActive !== false)
                .map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name || company.id}
                  </option>
                ))}
            </select>
          </label>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-100">
                <th className="px-3 py-2">資格名</th>
                <th className="px-3 py-2 text-right">自社価格</th>
                <th className="px-3 py-2 text-right">最安競合価格</th>
                <th className="px-3 py-2 text-right">差額</th>
                <th className="px-3 py-2">判定</th>
                <th className="px-3 py-2">主要競合</th>
                <th className="px-3 py-2">データ品質</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => (
                <tr key={row.shikakuId} className="border-b border-gray-50">
                  <td className="px-3 py-3 font-medium text-gray-900">{row.shikakuName}</td>
                  <td className="px-3 py-3 text-right">{row.ownPrice === null ? "-" : yen.format(row.ownPrice)}</td>
                  <td className="px-3 py-3 text-right">
                    {row.minCompetitorPrice === null ? "-" : yen.format(row.minCompetitorPrice)}
                  </td>
                  <td className={`px-3 py-3 text-right font-semibold ${row.status === "lose" ? "text-red-600" : "text-gray-900"}`}>
                    {row.priceDiff === null ? "-" : yen.format(row.priceDiff)}
                  </td>
                  <td className="px-3 py-3">
                    <span
                      className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                        row.status === "lose"
                          ? "bg-red-100 text-red-700"
                          : row.status === "win"
                            ? "bg-emerald-100 text-emerald-700"
                            : row.status === "tie"
                              ? "bg-gray-100 text-gray-700"
                              : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {statusLabelMap[row.status]}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-gray-700">{`${row.minCompetitorCompanyName} / ${row.minCompetitorName}`}</td>
                  <td className="px-3 py-3">
                    {row.dataQuality === "contains_fallback" ? (
                      <span className="text-amber-700 text-xs font-medium">取得失敗補完あり</span>
                    ) : (
                      <span className="text-emerald-700 text-xs font-medium">正常</span>
                    )}
                  </td>
                </tr>
              ))}
              {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-gray-500">
                    条件に合うデータがありません。
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
